import { Play } from 'lucide-react';

/**
 * Video container for the walkthrough's Step 2 / Step 3 (YAR-238 AC 12).
 *
 * Matches Figma spec 8662:74564 — a 210×446 portrait placeholder with a
 * 1.5px border and 24px corner radius, centered inside the modal. The
 * `shrink-0` is critical: the modal is a flex column with a max-height
 * cap, and without it the aspect-ratio box gets squished to a thin
 * horizontal strip (the bug George reported, where you only saw the
 * email body slice). `max-w-full` lets the container shrink on the
 * narrowest phones while keeping the aspect, never grows past 210.
 *
 * The MP4s in the `media` bucket are 496×1080 (~0.459) which is within
 * 3% of the Figma placeholder ratio (0.471), so object-cover crops at
 * most a couple of pixels off the top/bottom — invisible, and a safer
 * default than object-contain (no surface-secondary gutters when the
 * next re-record matches the placeholder exactly).
 */
export interface VideoPlaceholderProps {
  src?: string;
  poster?: string;
  ariaLabel: string;
}

const FRAME_CLASSES =
  'w-[210px] max-w-full aspect-[210/446] shrink-0 mx-auto rounded-3xl border-[1.5px] border-border bg-surface-secondary overflow-hidden';

export function VideoPlaceholder({
  src,
  poster,
  ariaLabel,
}: VideoPlaceholderProps) {
  if (src) {
    return (
      <div className={FRAME_CLASSES}>
        <video
          src={src}
          poster={poster}
          autoPlay
          loop
          muted
          playsInline
          aria-label={ariaLabel}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={`${FRAME_CLASSES} flex items-center justify-center`}
    >
      <Play
        size={48}
        className="text-icon-default"
      />
    </div>
  );
}
